import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Star, ArrowRight, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';

interface RecentWord {
  word: string;
  definition: string;
  stars: number;
  addedAgo: string;
}

interface RecentWordsProps {
  recentWords: RecentWord[];
}

export function RecentWords({ recentWords }: RecentWordsProps) {
  const navigate = useNavigate();

  return (
    <Card className="border-border/50 bg-card">
      <CardHeader className="flex flex-row items-center justify-between pb-4">
        <div>
          <CardTitle className="text-lg font-semibold text-foreground">
            Recent Words
          </CardTitle>
          <p className="mt-1 text-sm text-muted-foreground">
            Your latest vocabulary additions
          </p>
        </div>
        <Button
          variant="ghost"
          className="gap-2 text-primary hover:text-primary"
          onClick={() => navigate('/collection')}
        >
          View all
          <ArrowRight className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {recentWords.map((item) => (
            <div
              key={item.word}
              className="rounded-xl border border-border/50 bg-muted/30 p-4 flex flex-col min-h-[140px]"
            >
              <div className="mb-2 flex items-start justify-between">
                <h3 className="text-base font-semibold text-foreground">
                  {item.word}
                </h3>
                <div className="flex gap-0.5">
                  {Array.from({ length: item.stars }).map((_, i) => {
                    // Define star colors for different confidence levels (using grayscale hues)
                    const starColors = {
                      1: 'fill-gray-300 text-gray-300',
                      2: 'fill-gray-400 text-gray-400',
                      3: 'fill-gray-500 text-gray-500',
                      4: 'fill-gray-600 text-gray-600',
                      5: 'fill-gray-700 text-gray-700',
                    };

                    return (
                      <Star
                        key={i}
                        className={cn(
                          "h-3 w-3",
                          starColors[item.stars as keyof typeof starColors] || 'fill-muted text-muted'
                        )}
                      />
                    );
                  })}
                </div>
              </div>
              <p className="text-sm text-muted-foreground overflow-hidden mb-3" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                {item.definition}
              </p>
              <div className="flex items-center gap-1 text-xs text-muted-foreground/70 mt-auto">
                <Clock className="h-3 w-3" />
                {item.addedAgo}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}